import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useBuilding } from '../../Helpers/BuildingContext';
import { BaseComponentImpl } from '../../types/implementations';
import { BaseType } from '../../types/Enums';
import { BreadcrumbComponent, BreadcrumbItem } from '../../Components/BreadCrumb';
import ComponentCard from '../../Components/ComponentCard';
import ComponentDetailsView from '../ComponentPage/ComponentDetailsView';
import { translate } from '../../types/EnumTranslations';
import { Helmet } from 'react-helmet-async';

interface SheetMetalByParent {
    [parentKey: string]: {
        parent: BaseComponentImpl;
        sheetMetals: BaseComponentImpl[];
    };
}

const SheetMetalListPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const buildingKey = id || '';
    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();
    const [selectedSheetMetal, setSelectedSheetMetal] = useState<BaseComponentImpl | null>(null);

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };

        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    if (loadingBuilding) return <p></p>;
    if (buildingError) return <p style={{ color: "#ff0000" }}>{buildingError}</p>;
    if (!currentBuilding) return <p>No building data available</p>;
    if (!buildingKey) return <p>No building key provided</p>;

    // Group sheet metals by their parent components (Roof or Facade)
    const sheetMetalByParent: SheetMetalByParent = {};
    let totalSheetMetalCount = 0;

    if (currentBuilding.model?.structure?.components) {
        Object.values(currentBuilding.model.structure.components).forEach(comp => {
            if (comp.type === BaseType.SheetMetal) {
                totalSheetMetalCount++;
                
                // Get parent surface
                const parentKey = (comp as any).parentSurface?.value;

                if (parentKey) {
                    const parentComp = currentBuilding.model.structure?.components?.[parentKey];
                    if (parentComp) {
                        const parentType = parentComp.type;
                        
                        // Only add if parent is Roof or Facade
                        if (parentType === BaseType.Roof || parentType === BaseType.Facade) {
                            if (!sheetMetalByParent[parentKey]) {
                                sheetMetalByParent[parentKey] = {
                                    parent: new BaseComponentImpl(parentComp),
                                    sheetMetals: []
                                };
                            }
                            sheetMetalByParent[parentKey].sheetMetals.push(new BaseComponentImpl(comp));
                        }
                    }
                }
            }
        });
    }

    // Sort parents by type (Roof first, then Facade)
    const sortedParentKeys = Object.keys(sheetMetalByParent).sort((a, b) => {
        const parentA = sheetMetalByParent[a].parent;
        const parentB = sheetMetalByParent[b].parent;
        
        // Sort by type first (Roof before Facade)
        if (parentA.type === BaseType.Roof && parentB.type !== BaseType.Roof) return -1;
        if (parentA.type !== BaseType.Roof && parentB.type === BaseType.Roof) return 1;
        if (parentA.type === BaseType.Facade && parentB.type !== BaseType.Facade) return -1;
        if (parentA.type !== BaseType.Facade && parentB.type === BaseType.Facade) return 1;
        
        // Then by display name
        return parentA.displayName(currentBuilding).localeCompare(parentB.displayName(currentBuilding));
    });

    const crumbs: BreadcrumbItem[] = [
        {
            text: currentBuilding.summary.name ?? "Byggnad",
            link: `/building/${buildingKey}`
        },
        {
            text: translate(BaseType.SheetMetal)
        }
    ];

    return (
        <>
            <Helmet>
                <title>{currentBuilding?.summary?.name ? `${translate(BaseType.SheetMetal)} - ${currentBuilding.summary.name} - Molnfastigheter` : `${translate(BaseType.SheetMetal)} - Molnfastigheter`}</title>
            </Helmet>
            <BreadcrumbComponent items={crumbs} />
            <div style={{ padding: "20px", backgroundColor: "#f4f4f4" }}>
                <h3>{translate(BaseType.SheetMetal)} ({totalSheetMetalCount} st)</h3>

                {/* Two-pane layout */}
                <div style={{ display: 'flex', gap: '40px', width: '100%' }}>
                    {/* Left Pane - 60% width, max 900px */}
                    <div style={{ width: '60%', maxWidth: '900px', minWidth: '400px' }}>
                        <div style={{
                            width: '100%',
                            backgroundColor: 'white',
                            borderRadius: '10px',
                            padding: '40px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}>
                            {totalSheetMetalCount === 0 ? (
                                <p>Inga plåtarbeten finns registrerade för denna byggnad.</p>
                            ) : (
                                <>
                                    {sortedParentKeys.map(parentKey => {
                                        const { parent, sheetMetals } = sheetMetalByParent[parentKey];
                                        
                                        return (
                                            <div key={parentKey} style={{ marginBottom: '40px' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginBottom: '20px',
                                                    paddingBottom: '10px',
                                                    borderBottom: '2px solid #e0e0e0'
                                                }}>
                                                    <h5 style={{ margin: 0, marginRight: '10px', fontWeight: 500 }}>
                                                        {parent.displayName(currentBuilding)}
                                                    </h5>
                                                    <span style={{
                                                        backgroundColor: '#f0f0f0',
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontSize: '14px',
                                                        color: '#666'
                                                    }}>
                                                        {sheetMetals.length} st
                                                    </span>
                                                </div>

                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                                    gap: '20px'
                                                }}>
                                                    {sheetMetals.map(sheetMetal => (
                                                        <ComponentCard
                                                            key={sheetMetal.key}
                                                            component={sheetMetal}
                                                            building={currentBuilding}
                                                            buildingKey={buildingKey}
                                                            onNavigate={() => setSelectedSheetMetal(sheetMetal)}
                                                            isSelected={selectedSheetMetal?.key === sheetMetal.key}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Right Pane - Takes up remaining space, max 700px */}
                    <div style={{ flex: '1', minWidth: '300px', maxWidth: '700px' }}>
                        {selectedSheetMetal && currentBuilding ? (
                            <ComponentDetailsView
                                component={selectedSheetMetal}
                                building={currentBuilding}
                                buildingKey={buildingKey}
                                showHeader={true}
                                headerActions={
                                    <button
                                        onClick={() => window.open(`/building/${buildingKey}/component/${selectedSheetMetal.key}`, '_blank')}
                                        style={{
                                            padding: '8px',
                                            backgroundColor: 'transparent',
                                            color: '#666',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '32px',
                                            height: '32px'
                                        }}
                                        title="Öppna i ny flik"
                                    >
                                        ↗
                                    </button>
                                }
                                containerStyle={{
                                    position: 'sticky',
                                    top: '20px'
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    width: '100%',
                                    minHeight: '400px',
                                    border: '2px dashed #ccc',
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#fafafa',
                                    position: 'sticky',
                                    top: '20px'
                                }}
                            >
                                <p
                                    style={{
                                        color: '#666',
                                        fontSize: '16px',
                                        textAlign: 'center',
                                        margin: 0,
                                        padding: '20px'
                                    }}
                                >
                                    Klicka på ett plåtarbete för att se detaljer
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SheetMetalListPage;

